import moment from "moment";

export class PhoneNumberStatusUpdate {
  readonly date: Date = new Date();
  readonly previousStatus?: PhoneNumberStatus;
  readonly nextStatus: PhoneNumberStatus;

  constructor(deriveFrom?: Partial<PhoneNumber>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    this.date = new Date(this.date);
  }
}

/**
 * Represents a phone number with which a user may call.
 */
export default class PhoneNumber {
  readonly id: string;
  readonly phoneNumber: string;
  /**
   * Null means any project or none.
   */
  readonly projectIds?: string[] = null;
  readonly teamIds?: string[] = null;

  /**
   * Backtrace of phone numbers which were renewed.
   * 
   * If phone number A is renewed to B, and B to C, then C has a backtrace of A and B.
   */
  readonly replacedPhoneNumbers: string[] = [];
  readonly status: PhoneNumberStatus;
  readonly date: Date = new Date();
  readonly statusUpdates: PhoneNumberStatusUpdate[] = [];
  readonly isOutboundEnabled: boolean = true;
  readonly inboundConfiguration?: InboundConfiguration;
  readonly answeringMachineIds: string[] = [];

  constructor(deriveFrom?: Partial<PhoneNumber>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    this.date = new Date(this.date);

    this.statusUpdates = this.statusUpdates.map(statusUpdate => new PhoneNumberStatusUpdate(statusUpdate));
  }

  /**
   * @returns Whether this phone number can be used for making a call.
   */
  isUsable() {
    return this.status === PhoneNumberStatus.Ready || this.status === PhoneNumberStatus.Renewing;
  }

  isInboundEnabled() {
    return this.inboundConfiguration?.enabled;
  }

  /**
   * Whether the organization is billed for this phone number.
   */
  isBilledFor() {
    if (this.isUsable()) {
      return true;
    }

    const billedIfActiveAfter = moment().subtract(3, 'months').toDate();

    return Boolean(this.statusUpdates.find(update => (
      update.date > billedIfActiveAfter && update.previousStatus === PhoneNumberStatus.Ready
    )));
  }
}

export class InboundConfiguration {
  enabled: boolean;
}

export enum PhoneNumberStatus {
  Creating = 'creating',
  Ready = 'ready',
  Deleting = 'deleting',
  Deleted = 'deleted',
  Renewing = 'renewing',
}